import { useState } from 'react';
import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import { api, errorMessage } from '../lib/api';

const why = (o) => {
  if (o.how === 'number') return `${o.printed} is printed on the note`;
  if (o.how === 'supplier_ref') return `The supplier's order ${o.printed} is printed on the note`;
  const n = o.line_count;
  return `${n} ${n === 1 ? 'article' : 'articles'} on the note ${n === 1 ? 'is' : 'are'} open on this order: ${o.lines.join(', ')}${n > o.lines.length ? '…' : ''}`;
};

/* Open orders the delivery note mentions that were not picked. Inny reads
   the note in the background after saving and looks for our P-numbers, the
   supplier's own order numbers and articles open on another order of the
   same supplier. Add puts the note on that order too (PDF + WAREHOUSE
   RECEIVED, as if it had been picked); "Not in this delivery" hides it. */
export default function OtherOrders({ note, onChange, checking = false }) {
  const [labels, setLabels] = useState({});
  const [busy, setBusy] = useState(null); // po id being added or dismissed
  const [error, setError] = useState(null);
  const list = note.other_orders;

  if (list == null) {
    if (!checking) return null;
    return (
      <p className="mt-3 flex items-center gap-2 rounded-xl bg-sunken px-3 py-2 text-[13px] text-ink2">
        <Loader2 className="h-4 w-4 animate-spin" /> Reading the note to check for other orders on it…
      </p>
    );
  }
  if (!list.length) {
    return checking ? (
      <p className="mt-3 flex items-center gap-2 rounded-xl bg-sunken px-3 py-2 text-[13px] text-ink2">
        <Check className="h-4 w-4" /> No other open order found on the note
      </p>
    ) : null;
  }

  // everything from the note's own supplier in one go; another supplier's order stays a choice
  const own = list.filter((o) => !o.other_supplier);
  const allLabels = () => Object.fromEntries(own.map((o) => [o.id, (labels[o.id] || '').trim()]));
  const addAll = async () => {
    setBusy('all');
    setError(null);
    try {
      onChange(await api.addNoteOrders(note.id, own.map((o) => o.id), allLabels()));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };
  const dismissAll = async () => {
    setBusy('all');
    setError(null);
    try {
      onChange(await api.dismissNoteOrders(note.id, list.map((o) => o.id)));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const act = async (o, add) => {
    setBusy(o.id);
    setError(null);
    try {
      onChange(add ? await api.addNoteOrders(note.id, [o.id], { [o.id]: (labels[o.id] || '').trim() }) : await api.dismissNoteOrders(note.id, [o.id]));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="tone-over mt-3 rounded-2xl bg-tone-bg p-3">
      <p className="flex items-center gap-2 text-[15px] font-semibold text-tone">
        <AlertTriangle className="h-5 w-5 shrink-0" />
        {list.length === 1 ? 'Another order is on this note' : `${list.length} other orders are on this note`}
      </p>
      {list.length > 1 && (
        <div className="mt-2 flex gap-2">
          {own.length > 1 && (
            <button type="button" disabled={busy != null} onClick={addAll} className="h-12 flex-1 rounded-xl bg-ink text-[15px] font-semibold text-bg active:opacity-80 disabled:opacity-50">
              {busy === 'all' ? 'Adding…' : `Add all ${own.length}`}
            </button>
          )}
          <button type="button" disabled={busy != null} onClick={dismissAll} className="h-12 flex-1 rounded-xl bg-surface text-[14px] font-semibold text-ink2 active:bg-sunken disabled:opacity-50">
            None in this delivery
          </button>
        </div>
      )}
      <ul className="mt-2 space-y-2">
        {list.map((o) => (
          <li key={o.id} className="rounded-xl bg-surface p-3">
            <p className="font-cond text-[20px] font-bold leading-none">
              {o.name.split(' ')[0]} <span className="text-[14px] font-semibold text-ink2">{o.supplier}</span>
            </p>
            {o.other_supplier && <p className="mt-1 text-[13px] font-semibold text-alert">Another supplier than the note: check before adding</p>}
            <p className="mt-1 text-[13px] text-ink2">{why(o)}</p>
            <div className="mt-2 flex items-center gap-2">
              <input
                value={labels[o.id] || ''}
                onChange={(e) => setLabels((l) => ({ ...l, [o.id]: e.target.value.replace(/\D/g, '').slice(0, 3) }))}
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                aria-label={`Labels for ${o.name}`}
                className="h-11 w-16 rounded-xl border border-line bg-surface px-2 text-right text-[17px] font-semibold tabular-nums outline-none focus:border-ink"
              />
              <span className="text-[13px] text-ink2">labels</span>
              <button type="button" disabled={busy != null} onClick={() => act(o, true)} className="ml-auto inline-flex h-11 items-center rounded-xl bg-ink px-4 text-[15px] font-semibold text-bg active:opacity-80 disabled:opacity-50">
                {busy === o.id ? 'Adding…' : 'Add'}
              </button>
            </div>
            <button type="button" disabled={busy != null} onClick={() => act(o, false)} className="mt-2 h-10 w-full rounded-xl bg-sunken text-[14px] font-semibold text-ink2 active:bg-line disabled:opacity-50">
              Not in this delivery
            </button>
          </li>
        ))}
      </ul>
      {error && <p className="mt-2 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}
    </div>
  );
}
